/**
 * Inheritance badge — pill that says where an effective rep / bank value
 * came from in the lessor → property → room chain.
 *
 * Used on Property and Room detail screens next to the Bank / Representor
 * card. Per V2 handoff §7, "Inherited" when the value flows from a
 * higher-level default; "Override active" when this level has its own.
 */
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";

type Source =
  | "room"
  | "apartment"
  | "property"
  | "lessor"
  | "platform"
  | "none";

type Props = {
  /** Where the effective value resolved from. */
  source: Source;
  /** Which level we're rendering FOR — if it matches `source`, show "Override". */
  level: "property" | "apartment" | "room";
};

export function InheritanceBadge({ source, level }: Props) {
  const { t } = useLanguage();
  if (source === "none") {
    return (
      <Badge variant="outline" className="font-normal">
        {t("admin.inheritance.notSet", "Not set")}
      </Badge>
    );
  }
  if (source === level) {
    return (
      <Badge
        variant="default"
        className="font-normal bg-amber-500 hover:bg-amber-500/90"
      >
        {t("admin.inheritance.override", "Override active")}
      </Badge>
    );
  }
  const sourceLabel =
    source === "lessor"
      ? t("admin.inheritance.fromOwner", "from owner")
      : source === "property"
        ? t("admin.inheritance.fromProperty", "from property")
        : source === "apartment"
          ? t("admin.inheritance.fromApartment", "from apartment")
          : source === "platform"
            ? t("admin.inheritance.fromPlatform", "from coliviq")
            : source;
  return (
    <Badge variant="secondary" className="font-normal">
      {t("admin.inheritance.inherited", "Inherited")} {sourceLabel}
    </Badge>
  );
}
